/**
 * What the learner is told about syncing, and where conflicts get resolved.
 *
 * The status line is deliberately small and unexcited. It reports a fact and
 * never claims more than it knows: "Saved on this iPad" is not "Synced", and
 * neither is shown while the answer is still in flight. A status that overstates
 * once is a status nobody reads again.
 *
 * Conflicts are the one part that interrupts, because they are the one part that
 * cannot be resolved without a person. Both versions are offered; neither has
 * been discarded, and nothing was merged.
 */

import { useState } from 'react';
import { Button } from '../design/Button';
import { Alert, Card, Dialog } from '../design/Feedback';
import { Icon } from '../design/Icon';
import { useSync, describeSyncStatus } from './SyncProvider';
import type { ConflictRecord } from './types';

/** The quiet line. Safe to place next to a document title. */
export function SyncStatusLine() {
  const { engine, snapshot } = useSync();
  if (!engine) {
    // Signed out. Guest work is genuinely saved on this iPad and nowhere else,
    // which is what the local save status already says — so add nothing.
    return null;
  }

  const label = describeSyncStatus(snapshot.status, snapshot.pending);
  return (
    <p className={`mm-sync mm-sync--${snapshot.status}`} role="status">
      <span className="mm-sync__mark" aria-hidden="true">
        <Icon
          name={
            snapshot.status === 'needs-attention'
              ? 'alert'
              : snapshot.status === 'synced'
                ? 'check'
                : 'info'
          }
          size={15}
        />
      </span>
      {label}
    </p>
  );
}

/**
 * The conflict surface.
 *
 * Rendered wherever the learner already is, rather than as a console warning
 * nobody sees. Nothing is lost while it waits: both versions are on the iPad.
 */
export function ConflictNotice() {
  const { engine, snapshot } = useSync();
  const [open, setOpen] = useState<ConflictRecord | null>(null);
  const [busy, setBusy] = useState(false);

  if (!engine || snapshot.conflicts.length === 0) return null;

  const choose = async (conflict: ConflictRecord, keep: 'local' | 'remote') => {
    setBusy(true);
    try {
      await engine.resolveConflict(conflict.id, keep);
      setOpen(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Two versions of your work">
      <Alert tone="warning">
        {snapshot.conflicts.length === 1
          ? 'One piece of work was changed on this iPad and somewhere else.'
          : `${snapshot.conflicts.length} pieces of work were changed on this iPad and somewhere else.`}{' '}
        Both versions are safe. Choose which to carry on from.
      </Alert>

      <ul className="mm-conflicts">
        {snapshot.conflicts.map((conflict) => (
          <li key={conflict.id} className="mm-conflicts__item">
            <span className="mm-conflicts__name">{conflict.title}</span>
            <Button variant="secondary" onClick={() => setOpen(conflict)}>
              Choose a version
            </Button>
          </li>
        ))}
      </ul>

      {/* "Decide later" is the cancel action, not "keep the other version".
          Escape and a tap on the scrim both run cancel, and dismissing a dialog
          must never quietly choose which of two versions of somebody's
          handwriting to discard. Both real choices are explicit buttons. */}
      <Dialog
        open={open !== null}
        title={open ? `Which version of “${open.title}”?` : ''}
        confirmLabel="Keep this iPad's version"
        cancelLabel="Decide later"
        onConfirm={() => {
          if (open && !busy) void choose(open, 'local');
        }}
        onCancel={() => {
          if (!busy) setOpen(null);
        }}
      >
        <p>
          This work was changed in two places. Nothing has been merged and nothing has been
          deleted — handwriting cannot be combined automatically, so the choice is yours.
        </p>
        <p className="mm-conflicts__detail">
          Keeping this iPad's version sends it to your account. Keeping the version already in
          your account leaves it as it is. Either way the other copy stays on this iPad until you
          say otherwise.
        </p>
        <div className="mm-auth__actions">
          <Button
            variant="secondary"
            disabled={busy}
            onClick={() => {
              if (open && !busy) void choose(open, 'remote');
            }}
          >
            Keep the version in my account
          </Button>
        </div>
      </Dialog>
    </Card>
  );
}
